import React, { useState, useEffect, useRef } from 'react';
import { Input, Label, Textarea, Button, Toggle } from '../../ui';
import { AlertCircle, Loader2, Sparkles } from 'lucide-react';
import { getHelpCenter, updateHelpCenter, updateHelpCenterSettings } from '../../../services/api';
import type { HelpCenter, UpdateHelpCenterRequest } from '../../../types';
import toast from 'react-hot-toast';
import { HelpCenterBranding } from './HelpCenterBranding';
import { HelpCenterPasswordSettings } from './HelpCenterPasswordSettings';
import { HelpCenterLinksSettings } from './HelpCenterLinksSettings';
import { HelpCenterCustomDomain } from './HelpCenterCustomDomain';
import { useIsProCloudPlan } from '../../../store/userStore';
import { useUIStore } from '../../../store/uiStore';
import { Badge } from '../../ui/badge';
import UpsellModal from '../../modals/UpsellModal';

interface HelpCenterSettingsProps {
  helpCenterId: number | string;
  onSuccess?: () => void;
  onClose?: () => void;
}

export const HelpCenterSettings: React.FC<HelpCenterSettingsProps> = ({
  helpCenterId,
  onClose,
}) => {
  const [helpCenter, setHelpCenter] = useState<HelpCenter | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [activeTab, setActiveTab] = useState<'settings' | 'branding' | 'security' | 'links' | 'seo' | 'domain'>('settings');
  const isProCloudPlan = useIsProCloudPlan();
  const { openModal, modals, closeModal } = useUIStore();

  const [form, setForm] = useState({
    title: '',
    description: '',
    slug: '',
  });
  const [isIndexable, setIsIndexable] = useState(false);
  const [feedbackEnabled, setFeedbackEnabled] = useState(false);
  const [robotsTxt, setRobotsTxt] = useState('');
  const [isSavingRobotsTxt, setIsSavingRobotsTxt] = useState(false);
  const MAX_ROBOTS_TXT_LENGTH = 16 * 1024; // 16 KB
  const localSettingsRef = useRef({ isIndexable: false, feedbackEnabled: false });

  // Fetch help center data when component mounts
  useEffect(() => {
    if (helpCenterId) {
      fetchHelpCenter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [helpCenterId]);

  // Reset form when help center changes
  useEffect(() => {
    if (helpCenter) {
      setForm({
        title: helpCenter.title || '',
        description: helpCenter.description || '',
        slug: helpCenter.slug || '',
      });
      setSlugManuallyEdited(true);
      const indexableValue = Boolean(helpCenter.settings?.isIndexable);
      const feedbackEnabledValue = Boolean(helpCenter.settings?.feedbackEnabled);
      setIsIndexable(indexableValue);
      setFeedbackEnabled(feedbackEnabledValue);
      setRobotsTxt(typeof helpCenter.settings?.robotsTxt === 'string' ? helpCenter.settings.robotsTxt : '');
      localSettingsRef.current = { isIndexable: indexableValue, feedbackEnabled: feedbackEnabledValue };
    }
  }, [helpCenter]);

  const handleIndexableChange = (checked: boolean) => {
    setIsIndexable(checked);
    localSettingsRef.current = { ...localSettingsRef.current, isIndexable: checked };
  };

  const fetchHelpCenter = async () => {
    try {
      setLoading(true);
      setFormError(null);
      const response = await getHelpCenter(helpCenterId.toString());
      
      if (response.ok && response.helpCenter) {
        setHelpCenter(response.helpCenter);
      } else {
        const errorMessage = response.errors && response.errors.length > 0 
          ? response.errors[0] 
          : response.error || 'Failed to fetch help center';
        setFormError(errorMessage);
      }
    } catch (err) {
      console.error('Error fetching help center:', err);
      setFormError('Failed to fetch help center');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (title: string) => {
    setForm({
      ...form,
      title,
      // Only auto-generate slug if user hasn't manually edited it
      slug: slugManuallyEdited ? form.slug : generateSlug(title),
    });
    // Clear error when user starts typing
    if (formError) {
      setFormError(null);
    }
  };

  const handleSlugChange = (slug: string) => {
    setForm({ ...form, slug });
    // Mark slug as manually edited when user types in it
    setSlugManuallyEdited(true);
    // Clear error when user starts typing
    if (formError) {
      setFormError(null);
    }
  };

  const handleSettingsSubmit = async () => {
    setIsSubmitting(true);
    setFormError(null);

    try {
      const trimmedSlug = form.slug.trim();
      const originalSlug = helpCenter?.slug || '';
      
      // Only include slug in update if it was explicitly changed
      const updateData: UpdateHelpCenterRequest = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        // Only send slug if it's different from the original
        ...(trimmedSlug !== originalSlug && trimmedSlug ? { slug: trimmedSlug } : {}),
      };

      const response = await updateHelpCenter(helpCenterId.toString(), updateData);
      
      if (response.ok && response.helpCenter) {
        setHelpCenter(response.helpCenter);
        toast.success('Help center updated successfully', {
          position: 'top-center',
          duration: 3000,
        });
        // Don't call onSuccess for settings updates - it triggers full refresh and closes drawer
        // Only update local state to keep drawer open (similar to branding updates)
        // If slug changed, user will need to refresh manually or navigate
      } else {
        const errorMessage = response.errors && response.errors.length > 0 
          ? response.errors[0] 
          : response.error || 'Failed to update help center';
        setFormError(errorMessage);
      }
    } catch (err) {
      console.error('Error updating help center:', err);
      setFormError('Failed to update help center. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordUpdate = async (settings: {
    password?: string;
    passwordEnabled?: boolean;
    passwordValidDays?: number;
  }) => {
    try {
      const response = await updateHelpCenterSettings(
        helpCenterId.toString(),
        settings,
        undefined,
        false
      );
      
      if (response.ok && response.helpCenter) {
        setHelpCenter(response.helpCenter);
      } else {
        const errorMessage = response.errors && response.errors.length > 0 
          ? response.errors[0] 
          : response.error || 'Failed to update password settings';
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error updating password settings:', err);
      throw err;
    }
  };

  const handleBrandingUpdate = async (
    settings: {
      themeColor?: string;
      logoUrl?: string;
      companyUrl?: string;
    },
    logoFile?: File | null,
    removeFile?: boolean
  ) => {
    try {
      const response = await updateHelpCenterSettings(
        helpCenterId.toString(), 
        settings,
        logoFile,
        removeFile
      );
      
      if (response.ok && response.helpCenter) {
        setHelpCenter(response.helpCenter);
        toast.success('Branding updated successfully', {
          position: 'top-center',
          duration: 2000,
        });
        // Don't call onSuccess for branding updates - it triggers full refresh
        // Only update local state to keep drawer open
      } else {
        const errorMessage = response.errors && response.errors.length > 0 
          ? response.errors[0] 
          : response.error || 'Failed to update branding';
        toast.error(errorMessage, {
          position: 'top-center',
          duration: 3000,
        });
      }
    } catch (err) {
      console.error('Error updating branding:', err);
      toast.error('Failed to update branding. Please try again.', {
        position: 'top-center',
        duration: 3000,
      });
      throw err;
    }
  };

  const handleLinksUpdate = async (headerLinks: any[], footerLinks: any[]) => {
    try {
      const response = await updateHelpCenterSettings(
        helpCenterId.toString(),
        {
          headerLinks,
          footerLinks,
        },
        undefined,
        false
      );
      
      if (response.ok && response.helpCenter) {
        setHelpCenter(response.helpCenter);
      } else {
        const errorMessage = response.errors && response.errors.length > 0 
          ? response.errors[0] 
          : response.error || 'Failed to update links';
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error updating links:', err);
      toast.error('Failed to update links', {
        position: 'top-center',
        duration: 3000,
      });
      throw err;
    }
  };

  const handleSeoSave = async () => {
    if (robotsTxt.length > MAX_ROBOTS_TXT_LENGTH) {
      toast.error(`Custom robots.txt must be ${MAX_ROBOTS_TXT_LENGTH} characters or less`, {
        position: 'top-center',
        duration: 3000,
      });
      return;
    }
    setIsSavingRobotsTxt(true);
    try {
      const payload = {
        isIndexable,
        ...(isProCloudPlan ? { robotsTxt: robotsTxt.trim() } : {}),
      };
      const response = await updateHelpCenterSettings(
        helpCenterId.toString(),
        payload,
        undefined,
        false
      );
      if (response.ok && response.helpCenter) {
        setHelpCenter(response.helpCenter);
        setRobotsTxt(response.helpCenter.settings?.robotsTxt ?? '');
        setIsIndexable(response.helpCenter.settings?.isIndexable ?? false);
        localSettingsRef.current.isIndexable = response.helpCenter.settings?.isIndexable ?? false;
        toast.success('SEO settings saved', {
          position: 'top-center',
          duration: 3000,
        });
      } else {
        const errorMessage = response.errors?.length
          ? response.errors[0]
          : response.error || 'Failed to save SEO settings';
        toast.error(errorMessage, { position: 'top-center', duration: 3000 });
      }
    } catch (err) {
      console.error('Error saving SEO settings:', err);
      toast.error('Failed to save SEO settings. Please try again.', {
        position: 'top-center',
        duration: 3000,
      });
    } finally {
      setIsSavingRobotsTxt(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!helpCenter) {
    return (
      <div className="p-4">
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
          <p className="text-sm text-destructive">
            {formError || 'Help center not found'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Tabs */}
      <div className="flex border-b mb-4">
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'settings'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Settings
        </button>
        <button
          onClick={() => setActiveTab('branding')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'branding'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Branding
        </button>
        <button
          onClick={() => setActiveTab('security')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'security'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Security
        </button>
        <button
          onClick={() => setActiveTab('links')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'links'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Links
        </button>
        <button
          onClick={() => setActiveTab('seo')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'seo'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          SEO
        </button>
        <button
          onClick={() => setActiveTab('domain')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'domain'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Domain
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'settings' && (
          <div className="space-y-4">
            {/* Error Display */}
            {formError && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                <p className="text-sm text-destructive">{formError}</p>
              </div>
            )}

            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="My Help Center"
                className={`mt-1 ${formError ? 'border-destructive' : ''}`}
                disabled={isSubmitting}
              />
            </div>
            <div>
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={form.slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                placeholder="my-help-center"
                className={`mt-1 ${formError && formError.includes('Slug') ? 'border-destructive' : ''}`}
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground mt-1">
                URL-friendly identifier. Auto-generated from title if left empty.
              </p>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="A brief description of this help center"
                className="mt-1"
                rows={3}
                disabled={isSubmitting}
              />
            </div>

            {/* Feedback Collection */}
            <div className="md:col-span-5">
              <div className="space-y-2 flex flex-row items-center justify-between mt-5">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Enable Feedback Collection
                    </label>
                    {!isProCloudPlan && (
                      <Badge
                        variant="outline"
                        className="cursor-pointer border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 text-amber-700 hover:from-amber-100 hover:to-yellow-100 dark:border-amber-700 dark:from-amber-950/20 dark:to-yellow-950/20 dark:text-amber-300 dark:hover:from-amber-950/30 dark:hover:to-yellow-950/30"
                        onClick={() => openModal('upsell')}
                      >
                        <Sparkles className="h-3 w-3 mr-1" strokeWidth={2.5} />
                        Premium
                      </Badge>
                    )}
                  </div>
                  <p className="text-[0.8rem] text-muted-foreground">
                    {isProCloudPlan 
                      ? "Allow visitors to submit feedback about articles, guides, and categories"
                      : "This is a paid feature. Upgrade to enable feedback collection."}
                  </p>
                </div>
                <div className="ml-10 flex justify-between items-center">
                  <Toggle
                    checked={feedbackEnabled}
                    onChange={(checked) => {
                      if (!isProCloudPlan && checked) {
                        toast.error('Feedback collection is a paid feature. Please upgrade to enable it.', {
                          position: 'top-center',
                          duration: 3000,
                        });
                        return;
                      }
                      setFeedbackEnabled(checked);
                      localSettingsRef.current.feedbackEnabled = checked;
                      // Sync immediately (no debounce needed for toggle)
                      updateHelpCenterSettings(
                        helpCenterId.toString(),
                        { feedbackEnabled: checked },
                        undefined,
                        false
                      ).then((response) => {
                        if (response.ok && response.helpCenter) {
                          setHelpCenter(response.helpCenter);
                          toast.success('Feedback settings updated', {
                            position: 'top-center',
                            duration: 2000,
                          });
                        }
                      }).catch((err) => {
                        console.error('Error updating feedback settings:', err);
                        toast.error('Failed to update feedback settings', {
                          position: 'top-center',
                          duration: 3000,
                        });
                        // Revert on error
                        setFeedbackEnabled(!checked);
                        localSettingsRef.current.feedbackEnabled = !checked;
                      });
                    }}
                    disabled={!isProCloudPlan}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSettingsSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                Save Changes
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'branding' && (
          <div>
            <HelpCenterBranding
              settings={helpCenter.settings}
              isProCloudPlan={isProCloudPlan}
              onUpdate={handleBrandingUpdate}
            />
          </div>
        )}

        {activeTab === 'security' && (
          <div>
            <HelpCenterPasswordSettings
              settings={helpCenter.settings}
              isProCloudPlan={isProCloudPlan}
              onUpdate={handlePasswordUpdate}
            />
          </div>
        )}

        {activeTab === 'links' && (
          <div>
            <HelpCenterLinksSettings
              headerLinks={helpCenter.settings?.headerLinks || []}
              footerLinks={helpCenter.settings?.footerLinks || []}
              isProCloudPlan={isProCloudPlan}
              onUpdate={handleLinksUpdate}
            />
          </div>
        )}

        {activeTab === 'seo' && (
          <div className="space-y-4">
            <p className="text-[0.8rem] text-muted-foreground">
              Search indexing controls whether this help center can appear in search results. Custom robots.txt (optional) lets you define crawl rules, e.g. to block specific paths.
            </p>
            {/* Search indexing - available to all */}
            <div className="space-y-2 flex flex-row items-center justify-between">
              <div className="space-y-0.5">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Search indexing
                </label>
                <p className="text-[0.8rem] text-muted-foreground">
                  Allow search engines to index this help center
                </p>
              </div>
              <div className="ml-10 flex justify-between items-center">
                <Toggle
                  checked={isIndexable}
                  onChange={handleIndexableChange}
                />
              </div>
            </div>

            {/* Custom robots.txt - paid */}
            <div className={!isProCloudPlan ? 'pointer-events-none opacity-80' : ''}>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium leading-none">Custom robots.txt</label>
                {!isProCloudPlan && (
                  <Badge
                    variant="outline"
                    className="cursor-pointer border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 text-amber-700 hover:from-amber-100 hover:to-yellow-100 dark:border-amber-700 dark:from-amber-950/20 dark:to-yellow-950/20 dark:text-amber-300 dark:hover:from-amber-950/30 dark:hover:to-yellow-950/30"
                    onClick={() => !isProCloudPlan && openModal('upsell')}
                  >
                    <Sparkles className="h-3 w-3 mr-1" strokeWidth={2.5} />
                    Premium
                  </Badge>
                )}
              </div>
              <p className="text-[0.8rem] text-muted-foreground mt-1">
                {isProCloudPlan
                  ? 'Define a custom robots.txt for this help center. Crawlers will see it at your help center URL + /robots.txt. Leave empty to use the default (allow all).'
                  : 'Custom robots.txt is a paid feature. Upgrade to control how search engines crawl this help center.'}
              </p>
              <div className="relative mt-2">
                <Textarea
                  value={robotsTxt}
                  onChange={(e) => setRobotsTxt(e.target.value)}
                  placeholder={'User-agent: *\nAllow: /'}
                  className="font-mono text-sm min-h-[200px] resize-y"
                  disabled={!isProCloudPlan}
                  maxLength={MAX_ROBOTS_TXT_LENGTH}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {robotsTxt.length} / {MAX_ROBOTS_TXT_LENGTH} characters
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSavingRobotsTxt}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSeoSave}
                disabled={isSavingRobotsTxt}
                loading={isSavingRobotsTxt}
              >
                Save Changes
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'domain' && (
          <div>
            <HelpCenterCustomDomain
              helpCenterId={helpCenterId.toString()}
              isProCloudPlan={isProCloudPlan}
              currentDomain={helpCenter.settings?.customDomain}
            />
          </div>
        )}
      </div>

      {/* Upsell Modal */}
      <UpsellModal
        isOpen={modals.upsell}
        onClose={() => closeModal('upsell')}
      />
    </div>
  );
};

